import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Badge } from '../components/ui';
import { api } from '../services/api';
import { Order, OrderStatus } from '../types';
import { formatPrice, formatDate } from '../utils/helpers';

export const OrdersHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const ordersData = await api.getOrders();
      const sortedOrders = ordersData.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setOrders(sortedOrders);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: OrderStatus) => {
    const variants: Record<
      OrderStatus,
      { variant: 'success' | 'warning' | 'error' | 'info'; text: string }
    > = {
      completed: { variant: 'success', text: 'Выполнен' },
      ready: { variant: 'success', text: 'Готов' },
      awaiting_payment: { variant: 'warning', text: 'Ожидает оплаты' },
      pending: { variant: 'warning', text: 'Ожидает' },
      processing: { variant: 'info', text: 'Обработка' },
      manual: { variant: 'warning', text: 'Вручную' },
      failed: { variant: 'error', text: 'Ошибка' },
      cancelled: { variant: 'error', text: 'Отменен' },
    };

    const info = variants[status] || { variant: 'warning' as const, text: status };
    return (
      <Badge variant={info.variant} size="sm">
        {info.text}
      </Badge>
    );
  };

  if (isLoading) {
    return <Layout><div /></Layout>;
  }

  return (
    <Layout>
      <div className="px-4 py-6">
        {/* Header */}
        <div className="flex items-baseline justify-between mb-4" style={{ gap: '16px' }}>
          <h1 className="text-[22px] font-bold text-text-primary">
            История заказов
          </h1>
          <span className="text-sm text-text-secondary whitespace-nowrap">
            {orders.length} {orders.length === 1 ? 'заказ' : 'заказов'}
          </span>
        </div>

        {/* Orders List */}
        {orders.length > 0 ? (
          <div className="space-y-3">
            {orders.map((order) => {
              const firstItem = order.items[0];
              // Steam-заказы (в т.ч. старые, без items) — всегда с лого Steam
              const isSteamTopup = !!order.steamLogin || order.orderNumber.startsWith('ST-');
              const coverImage = isSteamTopup ? '/services/logo-steam.webp' : firstItem?.product?.coverImage;
              return (
                <div
                  key={order.id}
                  onClick={() => navigate(`/order-status/${order.id}`)}
                  className="flex items-center gap-4 p-3 rounded-2xl active:scale-[0.99] transition-all cursor-pointer"
                  style={{ background: '#F4F4F7' }}
                >
                  {coverImage && (
                    <img
                      src={coverImage}
                      alt=""
                      className="w-16 h-16 object-cover rounded-xl flex-shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '17px', lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
                        {order.orderNumber}
                      </span>
                      {getStatusBadge(order.status)}
                    </div>
                    <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#818384' }}>
                      {formatDate(order.createdAt)}  {formatPrice(order.totalAmount)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center px-6 text-center" style={{ minHeight: 'calc(100vh - 220px)' }}>
            <div className="flex items-center justify-center mb-5" style={{ width: '80px', height: '80px', borderRadius: '16px', background: '#F4F4F7' }}>
              <svg width="20" height="24" viewBox="0 0 20 24" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M12.3077 0.307692C12.3077 0.226087 12.2753 0.147825 12.2176 0.0901211C12.1599 0.0324176 12.0816 0 12 0H3.38462C2.48696 0 1.62607 0.356592 0.991331 0.991331C0.356592 1.62607 0 2.48696 0 3.38462V20.6154C0 21.513 0.356592 22.3739 0.991331 23.0087C1.62607 23.6434 2.48696 24 3.38462 24H15.6923C16.59 24 17.4509 23.6434 18.0856 23.0087C18.7203 22.3739 19.0769 21.513 19.0769 20.6154V8.48862C19.0769 8.40701 19.0445 8.32875 18.9868 8.27104C18.9291 8.21334 18.8508 8.18092 18.7692 8.18092H13.2308C12.986 8.18092 12.7512 8.08367 12.5781 7.91056C12.4049 7.73745 12.3077 7.50266 12.3077 7.25785V0.307692ZM13.2308 12.3077C13.4756 12.3077 13.7104 12.4049 13.8835 12.5781C14.0566 12.7512 14.1538 12.986 14.1538 13.2308C14.1538 13.4756 14.0566 13.7104 13.8835 13.8835C13.7104 14.0566 13.4756 14.1538 13.2308 14.1538H5.84615C5.60134 14.1538 5.36655 14.0566 5.19344 13.8835C5.02033 13.7104 4.92308 13.4756 4.92308 13.2308C4.92308 12.986 5.02033 12.7512 5.19344 12.5781C5.36655 12.4049 5.60134 12.3077 5.84615 12.3077H13.2308ZM13.2308 17.2308C13.4756 17.2308 13.7104 17.328 13.8835 17.5011C14.0566 17.6742 14.1538 17.909 14.1538 18.1538C14.1538 18.3987 14.0566 18.6335 13.8835 18.8066C13.7104 18.9797 13.4756 19.0769 13.2308 19.0769H5.84615C5.60134 19.0769 5.36655 18.9797 5.19344 18.8066C5.02033 18.6335 4.92308 18.3987 4.92308 18.1538C4.92308 17.909 5.02033 17.6742 5.19344 17.5011C5.36655 17.328 5.60134 17.2308 5.84615 17.2308H13.2308Z" fill="#818384"/>
                <path d="M14.1538 0.706521C14.1538 0.480059 14.3913 0.336059 14.5673 0.477598C14.7167 0.598213 14.8492 0.738521 14.9649 0.898521L18.6732 6.06406C18.7569 6.18221 18.6658 6.33483 18.5206 6.33483H14.4615C14.3799 6.33483 14.3016 6.30241 14.2439 6.24471C14.1862 6.187 14.1538 6.10874 14.1538 6.02714V0.706521Z" fill="#818384"/>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-text-primary mb-1.5">Заказов пока нет</h3>
            <p className="mb-6 max-w-[260px]" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', textAlign: 'center', color: '#818384' }}>Добавьте товары в корзину и оформите заказ</p>
            <button
              onClick={() => navigate('/home')}
              className="text-white font-bold active:scale-[0.97] transition-transform"
              style={{
                width: '240px',
                height: '44px',
                borderRadius: '12px',
                padding: '12px',
                fontSize: '15px',
                background: 'linear-gradient(90deg, #5370F5, #3657F0, #5370F5)',
              }}
            >
              Перейти в каталог
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};
